import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { ExamService } from '../../../Services/examServices';
import LiveExam from '../../../Services/liveexam.services';
import { errorResponseHelper } from '../../../helpers/request.helper';
import { defaultTheme, PageShell, TopBar, UserChip, Pill, NxButton, NxIcon } from '../../../components/nx';
import { AuthService } from '../../../Services/authServices';
import Auth from '../../../auth/auth';
import fingerPrint from '../../../helpers/fingerprintjs';
import AllPagesPDFViewer from '../Instructions/pdfview/AllPagesPdfViewer';
import { createGroupPaymentOrder, openGroupRazorpayCheckout } from '../../../api/razorpay';

const t = defaultTheme;

export default function Dashboard() {
    const navigate = useNavigate();
    const [examGroups, setExamGroups] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [enrollingId, setEnrollingId] = useState<string | null>(null);
    const [startingId, setStartingId] = useState<string | null>(null);
    const [certificateLoading, setCertificateLoading] = useState<string | null>(null);
    const [profile, setProfile] = useState<any>();
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [instructionModal, setInstructionModal] = useState<{ examId: string; pdf: string | null; loading: boolean } | null>(null);
    const [instructionAgreed, setInstructionAgreed] = useState(false);
    const [groupPaymentModal, setGroupPaymentModal] = useState<{
        group: any;
        paying: boolean;
    } | null>(null);
    const [verifyingPayment, setVerifyingPayment] = useState(false);
    const [paymentAgreed, setPaymentAgreed] = useState(false);


    async function fetchExams() {
        try {
            const res = await ExamService.getLiveExamsWithEnrollStatus();
            if (res.status === 200) {
                const groups = res.data.data.exams || [];
                setExamGroups(groups);
                // Auto-expand all groups
                setExpandedGroups(new Set(groups.map((g: any) => g.groupId)));
            }
        } catch (err) {
            toast.error('Failed to load exams');
        } finally {
            setLoading(false);
        }
    }

    async function getProfile() {
        try {
            const res = await ExamService.getUserInfoWtihoutExams();
            if (res.status === 200) {
                setProfile(res.data.data.studentInfo[0]);
            }
        } catch (e) { }
    }

    useEffect(() => {
        fetchExams();
        getProfile();
    }, []);

    async function openInstructionModal(examId: string) {
        setInstructionModal({ examId, pdf: null, loading: true });
        setInstructionAgreed(false);
        try {
            const res = await ExamService.getInstructions(examId);
            if (res.status === 200) {
                const pdf = res.data.data.instructions;
                setInstructionModal({ examId, pdf: pdf || null, loading: false });
            }
        } catch (e) {
            setInstructionModal({ examId, pdf: null, loading: false });
        }
    }

    async function handleEnroll(examId: string) {
        setEnrollingId(examId);
        try {
            const res = await ExamService.selfEnroll(examId);
            if (res.status === 200) {
                toast.success('Enrolled! Payment verification pending.');
                setInstructionModal(null);
                fetchExams();
            }
        } catch (err: any) {
            errorResponseHelper(err);
        } finally {
            setEnrollingId(null);
        }
    }

    async function generateTokenForLiveExam(examId: string, mappingId: string) {
        const res = await LiveExam.generateTokenForLiveExam(examId, mappingId);
        if (res.status === 200) {
            sessionStorage.setItem('liveToken', res.data.data.token);
            toast.success('Exam countdown has begun');
            navigate('/live/exam-live', { state: { examId, mappingId } });
        }
    }

    async function handleStart(examId: string, mappingId: string) {
        setStartingId(examId);
        try {
            const res = await ExamService.startExamId(examId);
            if (res.status === 200 && res.data) {
                if (res.data.examStart === true) {
                    await generateTokenForLiveExam(examId, mappingId);
                } else {
                    navigate('/exam/instructions', { state: { examId, mappingId } });
                }
            }
        } catch (err: any) {
            toast.error(err?.response?.data || 'Could not start exam');
        } finally {
            setStartingId(null);
        }
    }

    async function handleLogout() {
        try {
            const res = await AuthService.logout({ devInfo: await fingerPrint() });
            if (res.status === 200) {
                Auth.signout();
                localStorage.setItem("authKey", res.data.data.token);
                toast.success("Logged out");
            }
            navigate('/login');
        } catch (err) {
            navigate('/login');
        }
    }

    async function handleViewCertificate(mappingId: string) {
        setCertificateLoading(mappingId);
        try {
            const res = await ExamService.getCertificate(mappingId);
            if (res.status === 200 && res.data?.certificate) {
                let pdfData: string = res.data.certificate;
                // Strip data URI prefix if present
                if (pdfData.includes(',')) {
                    pdfData = pdfData.split(',')[1];
                }
                const byteCharacters = atob(pdfData);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const url = URL.createObjectURL(blob);
                window.open(url, '_blank');
            } else {
                toast.error('Certificate not available yet');
            }
        } catch (err: any) {
            console.log(err);
            toast.error(err?.response?.data?.message || 'Certificate not available');
        } finally {
            setCertificateLoading(null);
        }
    }



    // Open group payment modal
    function openGroupPaymentModal(group: any) {
        setPaymentAgreed(false);
        setGroupPaymentModal({ group, paying: false });
    }

    // Create group payment order and launch Razorpay checkout
    async function confirmGroupPayment() {
        if (!groupPaymentModal) return;
        setGroupPaymentModal({ ...groupPaymentModal, paying: true });
        try {
            const transaction = await createGroupPaymentOrder(groupPaymentModal.group.groupId);
            if (!transaction) {
                // Free group or already paid — just reload
                toast.success('Group access granted!');
                setGroupPaymentModal(null);
                fetchExams();
                return;
            }
            await openGroupRazorpayCheckout(
                transaction,
                {
                    name: profile?.name || 'Candidate',
                    email: profile?.email || '',
                    number: profile?.mobile || '',
                    applicationno: profile?.registrationNumber || '',
                },
                (verifying) => {
                    setVerifyingPayment(verifying);
                    if (verifying) setGroupPaymentModal(null);
                },
            );
        } catch (err: any) {
            errorResponseHelper(err);
        } finally {
            setGroupPaymentModal((prev) => (prev ? { ...prev, paying: false } : prev));
        }
    }

    const allExams = examGroups.flatMap((g: any) => g.exams || []);
    const totalExams = allExams.length;

    const toggleGroup = (groupId: string) => {
        setExpandedGroups(prev => {
            const next = new Set(prev);
            if (next.has(groupId)) next.delete(groupId);
            else next.add(groupId);
            return next;
        });
    };

    const profileName = profile ? `${profile.name || ''} ${profile.last_name || ''}`.trim() : 'User';
    const isApproved = profile?.isApproved === true;

    return (
        <PageShell t={t} style={{ display: 'flex', flexDirection: 'column', height: '100vh' }}>
            <TopBar
                t={t}
                crumbs={['Dashboard']}
                right={<>
                    <button
                        onClick={() => navigate('/exam/user/profile')}
                        style={{ width: 32, height: 32, borderRadius: 8, background: 'transparent', border: `1px solid ${t.border}`, color: t.inkSoft, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                    >
                        {NxIcon.info(t.inkSoft)}
                    </button>
                    <UserChip t={t} name={profileName} email={profile?.email} onLogout={handleLogout} />
                </>}
            />

            <div style={{ flex: 1, overflow: 'auto', padding: '28px 40px 40px' }}>
                {/* Greeting */}
                <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22 }}>
                    <div>
                        <div style={{ fontSize: 12, color: t.inkMute, letterSpacing: '0.04em', textTransform: 'uppercase', fontWeight: 500 }}>
                            {new Date().toLocaleDateString('en-US', { weekday: 'long', day: 'numeric', month: 'long' })}
                        </div>
                        <h1 style={{
                            // fontFamily: '"Instrument Serif", serif',
                            fontSize: 34, fontWeight: 400, lineHeight: 1.1, letterSpacing: '-0.01em',
                            color: t.ink, margin: '6px 0 0',
                        }}>
                            Good {new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 17 ? 'afternoon' : 'evening'}, {profile?.name || 'there'}.
                        </h1>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 18, fontSize: 13 }}>
                        {[
                            { l: 'Groups', v: examGroups.length },
                            { l: 'Exams', v: totalExams },
                            { l: 'Enrolled', v: allExams.filter(e => e.isEnrolled).length },
                            { l: 'Completed', v: allExams.filter(e => e.completionStatus).length },
                        ].map((s, i) => (
                            <div key={i} style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                                <span style={{ fontSize: 22, fontWeight: 500, color: t.ink, letterSpacing: '-0.01em', fontVariantNumeric: 'tabular-nums' }}>{s.v}</span>
                                <span style={{ fontSize: 11, color: t.inkMute, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{s.l}</span>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Approval pending banner */}
                {!loading && profile && !isApproved && (
                    <div style={{
                        background: '#FFF8E1', border: '1px solid #FFE082', borderRadius: 10,
                        padding: '14px 20px', marginBottom: 18,
                        display: 'flex', alignItems: 'center', gap: 12,
                    }}>
                        <span style={{ fontSize: 20 }}>&#9888;</span>
                        <div>
                            <div style={{ fontSize: 14, fontWeight: 600, color: '#E65100' }}>Registration Pending Approval</div>
                            <div style={{ fontSize: 13, color: '#6D4C00', marginTop: 2 }}>
                                Your registration documents are under review. You will be able to enroll in exams once approved by the admin.
                            </div>
                        </div>
                    </div>
                )}

                {/* Section header */}
                <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginTop: 36, marginBottom: 14 }}>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 12 }}>
                        <h2 style={{ fontSize: 16, fontWeight: 500, color: t.ink, margin: 0, letterSpacing: '-0.005em' }}>Exam Groups</h2>
                        <span style={{ fontSize: 12, color: t.inkMute }}>{examGroups.length} groups, {totalExams} exams</span>
                    </div>
                </div>

                {/* Groups */}
                {loading ? (
                    <div style={{ textAlign: 'center', padding: 40, color: t.inkMute }}>Loading...</div>
                ) : examGroups.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: 40, color: t.inkMute, fontSize: 13 }}>No exam groups available</div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {examGroups.map((group: any) => {
                            const isExpanded = expandedGroups.has(group.groupId);
                            const exams = group.exams || [];
                            return (
                                <div key={group.groupId} style={{
                                    background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: 'hidden',
                                }}>
                                    {/* Group header */}
                                    <div
                                        style={{
                                            padding: '16px 20px',
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            cursor: 'pointer', background: t.paperAlt,
                                            borderBottom: isExpanded ? `1px solid ${t.border}` : 'none',
                                        }}
                                        onClick={() => toggleGroup(group.groupId)}
                                    >
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                                                width: 20, height: 20, fontSize: 12, color: t.inkSoft,
                                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                                transition: 'transform 0.2s',
                                            }}>&#9654;</span>
                                            <div>
                                                <div style={{ fontSize: 15, fontWeight: 600, color: t.ink }}>{group.groupName}</div>
                                                <div style={{ fontSize: 12, color: t.inkMute, marginTop: 2 }}>
                                                    {exams.length} exam{exams.length !== 1 ? 's' : ''}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }} onClick={(e) => e.stopPropagation()}>
                                            {group.isGroupPaid ? (
                                                <Pill t={t} tone="enrolled">Paid</Pill>
                                            ) : group.groupFees > 0 ? (
                                                <button
                                                    onClick={() => isApproved ? openGroupPaymentModal(group) : toast.error('Your registration is pending approval.')}
                                                    style={{
                                                        height: 32, padding: '0 16px', borderRadius: 7,
                                                        border: 'none', background: t.accent, color: '#fff',
                                                        fontFamily: 'inherit', fontSize: 12, fontWeight: 600, cursor: isApproved ? 'pointer' : 'not-allowed',
                                                        display: 'inline-flex', alignItems: 'center', gap: 6,
                                                        opacity: isApproved ? 1 : 0.6,
                                                    }}
                                                >
                                                    Pay ₹{group.groupFees.toLocaleString('en-IN')} {NxIcon.arrowRight('#fff')}
                                                </button>
                                            ) : (
                                                <Pill t={t} tone="enrolled">Free</Pill>
                                            )}
                                        </div>
                                    </div>

                                    {/* Exam rows inside group */}
                                    {isExpanded && (
                                        <div>
                                            {/* Column headers */}
                                            <div style={{
                                                display: 'grid', gridTemplateColumns: '1.6fr 110px 130px 130px',
                                                padding: '10px 20px', gap: 16,
                                                borderBottom: `1px solid ${t.border}`,
                                                fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: t.inkMute, fontWeight: 600,
                                            }}>
                                                <span>Exam</span>
                                                <span>Subject</span>
                                                <span>Status</span>
                                                <span style={{ textAlign: 'right' }}>Action</span>
                                            </div>

                                            {exams.length === 0 ? (
                                                <div style={{ padding: '20px', textAlign: 'center', color: t.inkMute, fontSize: 13 }}>
                                                    No exams in this group
                                                </div>
                                            ) : exams.map((exam: any, i: number) => (
                                                <div key={exam._id} style={{
                                                    display: 'grid', gridTemplateColumns: '1.6fr 110px 130px 130px',
                                                    padding: '14px 20px', gap: 16, alignItems: 'center',
                                                    borderBottom: i < exams.length - 1 ? `1px solid ${t.border}` : 'none',
                                                }}>
                                                    <div style={{ minWidth: 0 }}>
                                                        <div style={{ fontSize: 13.5, color: t.ink, fontWeight: 500, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                            {exam.examName}
                                                        </div>
                                                        <div style={{ fontSize: 11, color: t.inkMute, marginTop: 2 }}>
                                                            {exam.totalQuestions || '—'} questions
                                                        </div>
                                                    </div>
                                                    <span style={{ fontSize: 13, color: t.inkSoft }}>{exam.subject || '—'}</span>
                                                    <span>
                                                        <Pill t={t} tone={exam.isEnrolled ? (exam.completionStatus ? 'neutral' : 'enrolled') : 'notenrolled'}>
                                                            {exam.completionStatus ? 'Completed' : exam.isEnrolled ? 'Enrolled' : 'Not enrolled'}
                                                        </Pill>
                                                    </span>
                                                    <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                                        {renderAction(exam, group.isGroupPaid)}
                                                    </span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* Instruction modal before enrol */}
            {instructionModal && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 9999,
                    background: 'rgba(0,0,0,0.5)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <div style={{
                        background: t.surface, borderRadius: 14, width: '90%', maxWidth: 800,
                        maxHeight: '85vh', display: 'flex', flexDirection: 'column',
                        overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                    }}>
                        <div style={{
                            padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Exam Instructions</h3>
                            <button
                                onClick={() => setInstructionModal(null)}
                                style={{
                                    background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                    padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                }}
                            >Close</button>
                        </div>

                        <div style={{ flex: 1, overflow: 'auto', padding: 24, display: 'flex', justifyContent: 'center' }}>
                            {instructionModal.loading ? (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>Loading instructions...</div>
                            ) : instructionModal.pdf ? (
                                <AllPagesPDFViewer pdf={instructionModal.pdf} width={720} />
                            ) : (
                                <div style={{ padding: 40, color: t.inkMute, fontSize: 14 }}>No instructions available for this exam.</div>
                            )}
                        </div>

                        <div style={{
                            padding: '14px 24px', borderTop: `1px solid ${t.border}`,
                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        }}>
                            <label
                                style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13, color: t.inkSoft, cursor: 'pointer' }}
                                onClick={() => setInstructionAgreed(!instructionAgreed)}
                            >
                                <span style={{
                                    width: 16, height: 16, borderRadius: 4,
                                    background: instructionAgreed ? t.accent : 'transparent',
                                    border: instructionAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    {instructionAgreed && NxIcon.check('#fff')}
                                </span>
                                I have read and understood the instructions.
                            </label>
                            <NxButton
                                t={t} kind="primary"
                                disabled={!isApproved || !instructionAgreed || enrollingId === instructionModal.examId}
                                onClick={() => isApproved ? handleEnroll(instructionModal.examId) : toast.error('Your registration is pending approval. You cannot enroll until approved by admin.')}
                            >
                                {enrollingId === instructionModal.examId ? 'Enrolling...' : !isApproved ? 'Approval Pending' : 'Proceed to Enroll'}
                            </NxButton>
                        </div>
                    </div>
                </div>
            )}

            {/* Group payment confirmation modal */}
            {groupPaymentModal && (() => {
                const group = groupPaymentModal.group;
                const groupFee = group.groupFees || 0;
                const exams = group.exams || [];
                return (
                    <div style={{
                        position: 'fixed', inset: 0, zIndex: 9999,
                        background: 'rgba(0,0,0,0.5)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }} onClick={() => !groupPaymentModal.paying && setGroupPaymentModal(null)}>
                        <div
                            onClick={(e) => e.stopPropagation()}
                            style={{
                                background: t.surface, borderRadius: 14, width: '90%', maxWidth: 560,
                                display: 'flex', flexDirection: 'column',
                                overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
                            }}
                        >
                            {/* Header */}
                            <div style={{
                                padding: '18px 24px', borderBottom: `1px solid ${t.border}`,
                                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                            }}>
                                <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: t.ink }}>Pay for Exam Group</h3>
                                <button
                                    onClick={() => setGroupPaymentModal(null)}
                                    disabled={groupPaymentModal.paying}
                                    style={{
                                        background: 'transparent', border: `1px solid ${t.border}`, borderRadius: 6,
                                        padding: '4px 12px', fontSize: 12, color: t.inkSoft, cursor: 'pointer', fontFamily: 'inherit',
                                    }}
                                >Close</button>
                            </div>

                            {/* Body */}
                            <div style={{ padding: 24, maxHeight: '68vh', overflowY: 'auto' }}>
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 6 }}>
                                    Exam Group
                                </div>
                                <div style={{ fontSize: 18, fontWeight: 500, color: t.ink, lineHeight: 1.3, marginBottom: 16 }}>
                                    {group.groupName}
                                </div>

                                {/* Exams included */}
                                <div style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: t.inkMute, marginBottom: 8 }}>
                                    Exams included ({exams.length})
                                </div>
                                <div style={{ background: t.paperAlt, borderRadius: 10, padding: '4px 16px', marginBottom: 16 }}>
                                    {exams.map((exam: any, i: number) => (
                                        <div key={exam._id} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                                            padding: '10px 0',
                                            borderBottom: i < exams.length - 1 ? `1px solid ${t.border}` : 'none',
                                        }}>
                                            <span style={{ fontSize: 13, color: t.ink }}>{exam.examName}</span>
                                            <span style={{ fontSize: 12, color: t.inkMute }}>{exam.subject || ''}</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Detail rows */}
                                <div style={{ background: t.paperAlt, borderRadius: 10, padding: '4px 16px' }}>
                                    {[
                                        { label: 'Candidate', value: profile?.name || '—' },
                                        { label: 'Registration No.', value: profile?.registrationNumber || '—' },
                                    ].map((row, i, arr) => (
                                        <div key={row.label} style={{
                                            display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12,
                                            padding: '12px 0',
                                            borderBottom: i < arr.length - 1 ? `1px solid ${t.border}` : 'none',
                                        }}>
                                            <span style={{ fontSize: 13, color: t.inkSoft }}>{row.label}</span>
                                            <span style={{ fontSize: 13, color: t.ink, fontWeight: 500, textAlign: 'right', maxWidth: '60%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                {row.value}
                                            </span>
                                        </div>
                                    ))}
                                </div>

                                {/* Amount */}
                                <div style={{
                                    display: 'flex', justifyContent: 'space-between', alignItems: 'baseline',
                                    marginTop: 20, paddingTop: 16, borderTop: `1px solid ${t.border}`,
                                }}>
                                    <span style={{ fontSize: 14, color: t.inkSoft }}>Amount to pay</span>
                                    <span style={{ fontSize: 26, fontWeight: 600, color: t.ink, letterSpacing: '-0.01em' }}>
                                        ₹{groupFee.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                    </span>
                                </div>

                                {/* Agree checkbox */}
                                <label
                                    onClick={() => setPaymentAgreed(v => !v)}
                                    style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginTop: 16, cursor: 'pointer' }}
                                >
                                    <span style={{
                                        width: 18, height: 18, borderRadius: 4, flexShrink: 0, marginTop: 1,
                                        background: paymentAgreed ? t.accent : 'transparent',
                                        border: paymentAgreed ? 'none' : `1px solid ${t.borderStrong}`,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        {paymentAgreed && NxIcon.check('#fff')}
                                    </span>
                                    <span style={{ fontSize: 13, color: t.inkSoft, lineHeight: 1.5 }}>
                                        I agree to pay for this exam group and understand that this gives me access to enroll in all exams within this group.
                                    </span>
                                </label>
                            </div>

                            {/* Footer */}
                            <div style={{ padding: '14px 24px', borderTop: `1px solid ${t.border}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                                <NxButton t={t} kind="secondary" disabled={groupPaymentModal.paying} onClick={() => setGroupPaymentModal(null)}>
                                    Cancel
                                </NxButton>
                                <NxButton
                                    t={t} kind="primary"
                                    disabled={groupPaymentModal.paying || !paymentAgreed}
                                    onClick={confirmGroupPayment}
                                >
                                    {groupPaymentModal.paying
                                        ? 'Opening…'
                                        : `Pay ₹${groupFee.toLocaleString('en-IN')}`}
                                </NxButton>
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Payment verification loader */}
            {verifyingPayment && (
                <div style={{
                    position: 'fixed', inset: 0, zIndex: 10000,
                    background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(2px)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    <style>{`@keyframes nx-spin { to { transform: rotate(360deg); } }`}</style>
                    <div style={{
                        background: t.surface, borderRadius: 14, padding: '32px 40px',
                        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 16,
                        boxShadow: '0 20px 60px rgba(0,0,0,0.25)', maxWidth: 320, textAlign: 'center',
                    }}>
                        <div style={{
                            width: 40, height: 40, borderRadius: '50%',
                            border: `3px solid ${t.border}`, borderTopColor: t.accent,
                            animation: 'nx-spin 0.8s linear infinite',
                        }} />
                        <div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: t.ink }}>Verifying payment</div>
                            <div style={{ fontSize: 13, color: t.inkMute, marginTop: 4, lineHeight: 1.5 }}>
                                Please wait and do not close this window.
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </PageShell>
    );

    function renderAction(exam: any, isGroupPaid: boolean) {
        if (!exam.isEnrolled) {
            if (!isGroupPaid) {
                return (
                    <span style={{ fontSize: 11, color: t.inkMute, whiteSpace: 'nowrap' }}>
                        Pay for group first
                    </span>
                );
            }
            return (
                <button
                    onClick={() => openInstructionModal(exam._id)}
                    style={{
                        height: 32, padding: '0 14px', borderRadius: 7,
                        border: `1px solid ${t.borderStrong}`, background: 'transparent',
                        color: t.ink,
                        fontFamily: 'inherit', fontSize: 12, fontWeight: 500,
                        cursor: 'pointer',
                    }}
                >
                    Enroll
                </button>
            );
        }

        if (exam.completionStatus) {
            return (
                <div style={{ display: 'flex', gap: 6 }}>
                    <button
                        disabled
                        style={{
                            height: 32, padding: '0 14px', borderRadius: 7,
                            border: `1px solid ${t.border}`, background: 'transparent', color: t.inkMute,
                            fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'not-allowed',
                        }}
                    >
                        Completed
                    </button>
                    {exam.mappingId && (
                        <button
                            onClick={() => handleViewCertificate(exam.mappingId)}
                            disabled={certificateLoading === exam.mappingId}
                            style={{
                                height: 32, padding: '0 14px', borderRadius: 7,
                                border: `1px solid ${t.accent}`, background: 'transparent', color: t.accent,
                                fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                                opacity: certificateLoading === exam.mappingId ? 0.5 : 1,
                            }}
                        >
                            {certificateLoading === exam.mappingId ? 'Loading...' : 'Certificate'}
                        </button>
                    )}
                </div>
            );
        }

        if (!exam.mappingActive) {
            return (
                <span
                    title="Your exam access has not been activated by the invigilator yet."
                    style={{ fontSize: 11, color: t.inkMute, whiteSpace: 'nowrap' }}
                >
                    Awaiting activation
                </span>
            );
        }

        return (
            <button
                onClick={() => handleStart(exam._id, exam.mappingId)}
                disabled={startingId === exam._id}
                style={{
                    height: 32, padding: '0 14px', borderRadius: 7,
                    border: 'none', background: t.ink, color: t.paper,
                    fontFamily: 'inherit', fontSize: 12, fontWeight: 500, cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    opacity: startingId === exam._id ? 0.5 : 1,
                }}
            >
                {startingId === exam._id ? 'Starting...' : <>Continue {NxIcon.arrowRight(t.paper)}</>}
            </button>
        );
    }
}
